type FbType = typeof FB;
type ResolveFb = () => FbType;

class FacebookOauthService {
  private readonly resolveFb: ResolveFb;
  private readonly appId: string;
  private readonly scope: string;

  constructor({ resolveFb, appId, scope }: { resolveFb: ResolveFb; appId: string; scope: string }) {
    this.resolveFb = resolveFb;
    this.appId = appId;
    this.scope = scope;
  }

  public authenticate = async (): Promise<string> => {
    await this.initialize();

    return new Promise<string>((resolve, reject) => {
      const fb = this.getFb();

      fb.login(
        (response) => {
          if (!response.authResponse) {
            reject(new AuthenticationCancelled());

            return;
          }

          resolve(response.authResponse.accessToken);
        },
        { scope: this.scope },
      );
    });
  };

  private getFb = (): FbType => {
    const fb = this.resolveFb();

    if (fb === undefined) {
      throw new Error('resolveFb() returns undefined.');
    }

    return fb;
  };

  private initialize = async () => {
    const fb = this.resolveFb();
    if (fb) {
      fb.init({
        appId: this.appId,
        xfbml: false,
        version: 'v6.0',
      });
      return;
    } else {
      return new Promise<void>((resolve) => {
        // window.FB is not ready. Let's wait for loading.
        // `fbAsyncInit` function will be invoked after `window.FB` has been defined.
        // Documentation: https://developers.facebook.com/docs/javascript/advanced-setup#onload
        (window as any).fbAsyncInit = async () => {
          await this.initialize();
          resolve();
        };
      });
    }
  };
}

export class AuthenticationCancelled extends Error {
  public name = 'AuthenticationCancelled';
  public message = 'Authentication via Facebook is cancelled.';

  constructor() {
    super();
  }
}

export default FacebookOauthService;
